import { PieChart, Pie, Cell, Legend, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface Investment {
  id: string;
  fund_manager: string;
  balance: number;
  currency: string;
  convertedBalance?: number;
  mutual_fund_metadata?: { fund_category?: string } | null;
}

interface InvestmentDistributionChartProps {
  investments: Investment[];
  currency: string;
  isLoading: boolean;
  title?: string;
  /** Field to group by. Defaults to 'fund_manager'. Use 'fund_category' for category distribution. */
  groupBy?: 'fund_manager' | 'fund_category';
}

const COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FFC658', '#FF6B9D', '#C084FC', '#34D399'
];

/**
 * InvestmentDistributionChart component displays a pie chart showing investment
 * allocation across fund managers with percentage breakdowns.
 * 
 * @param investments - Array of investment accounts to visualize
 * @param currency - The selected currency for display
 * @param isLoading - Loading state indicator
 * @param title - Optional title to display, default is `Investment Distribution by Fund Manager`
 * 
 * @example
 * <InvestmentDistributionChart
 *   investments={investments}
 *   currency="USD"
 *   isLoading={false}
 * />
 */
export function InvestmentDistributionChart({
  investments,
  currency,
  isLoading,
  title = 'Investment Distribution by Fund Manager',
  groupBy = 'fund_manager',
}: InvestmentDistributionChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Investment Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!investments || investments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Investment Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No investments to display
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group investments by the selected field and calculate totals using converted balances if available
  const groupedData = investments.reduce((acc, investment) => {
    const key =
      groupBy === 'fund_category'
        ? (investment.mutual_fund_metadata?.fund_category ?? 'Unknown')
        : investment.fund_manager;
    if (!acc[key]) {
      acc[key] = 0;
    }
    const balanceToUse = investment.convertedBalance !== undefined ? investment.convertedBalance : investment.balance;
    acc[key] += balanceToUse;
    return acc;
  }, {} as Record<string, number>);

  // Calculate total for percentage calculation
  const total = Object.values(groupedData).reduce((sum, value) => sum + value, 0);

  // Transform to chart data format with percentages
  const chartData = Object.entries(groupedData).map(([name, value], index) => ({
    name,
    value,
    percentage: ((value / total) * 100).toFixed(2),
    color: COLORS[index % COLORS.length]
  }));

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ percentage }) => `${percentage}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => formatCurrency(value)}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
